import {Children, Vnode, VnodeDOM} from 'mithril';
import app from 'flarum/forum/app';
import Button from 'flarum/common/components/Button';
import Select from 'flarum/common/components/Select';
import Switch from 'flarum/common/components/Switch';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import icon from 'flarum/common/helpers/icon';
import extractText from 'flarum/common/utils/extractText';
import withAttr from 'flarum/common/utils/withAttr';
import Group from 'flarum/common/models/Group';
import FieldList from '../fields/FieldList';
import ValueHistory from '../utils/ValueHistory';
import AbstractFormPage from './AbstractFormPage';
import SaveControls from '../components/SaveControls';
import FormGroup from '../components/FormGroup';
import RichTextField from '../components/RichTextField';
import PageContainer from '../components/PageContainer';
import AutomaticDiscussionOptionsModal from '../modals/AutomaticDiscussionOptionsModal';
import ConfirmationEmailOptionsModal from '../modals/ConfirmationEmailOptionsModal';
import FormEditHeader from '../components/FormEditHeader';
import PermissionDropdown from '../components/PermissionDropdown';
import Form from '../models/Form';
import {AutomaticDiscussionOptions} from '../models/JsonTypes';
import {formulaireFormShow} from '../routes';
import composerPageExtensionLink from '../../common/helpers/composerPageExtensionLink';

const translationPrefix = 'kilowhat-formulaire.forum.form-edit.';

export default class FormEditPage extends AbstractFormPage {
    tab: string = 'template';
    history!: ValueHistory
    windowOnbeforeunload: any
    tagsLoading: boolean = false
    tagsLoaded: boolean = false

    oninit(vnode: Vnode) {
        super.oninit(vnode);

        // Used to navigate to a tab directly from another page
        if (app.cache.formulaireFormEditTab) {
            this.tab = app.cache.formulaireFormEditTab as any;
            delete app.cache.formulaireFormEditTab;
        }
    }

    show(form: Form) {
        // Create a copy of the model
        // That way if we leave the page without saving, the data isn't persisted in the store
        // @ts-ignore copyData is protected
        this.form = app.store.createRecord<Form>('formulaire-forms', form.copyData());
        this.form.exists = true;

        this.history = new ValueHistory(form.template());

        this.loadTagsIfNecessary();
    }

    oncreate(vnode: VnodeDOM) {
        super.oncreate(vnode);

        // Handles quitting the page by navigating/closing
        this.windowOnbeforeunload = (event: Event) => {
            if (this.dirty) {
                event.preventDefault();
                event.returnValue = extractText(app.translator.trans(translationPrefix + 'exiting-with-unsaved-data')) as any;
            }
        }

        window.addEventListener('beforeunload', this.windowOnbeforeunload);
    }

    onbeforeremove() {
        // Handles quitting the page by navigating inside the single page app
        if (this.dirty && !confirm(extractText(app.translator.trans(translationPrefix + 'exiting-with-unsaved-data')))) {
            return Promise.reject();
        }

        window.removeEventListener('beforeunload', this.windowOnbeforeunload);

        return Promise.resolve();
    }

    onremove() {
        window.removeEventListener('beforeunload', this.windowOnbeforeunload);
    }

    content(form: Form): Children {
        if (!form.attribute('canEdit')) {
            return m(PageContainer, 'Cannot edit');//TODO: translation
        }

        const warnings = this.warnings(form);

        return m(PageContainer, [
            m(FormEditHeader, {
                form,
                tab: this.tab,
                changeTab: (key: string) => {
                    this.tab = key;

                    this.loadTagsIfNecessary();
                },
                editable: true,
                updateAttribute: this.updateAttribute.bind(this),
                validationErrors: this.validationErrors,
            }),
            warnings.map(warning => m('.Alert.Alert--warning', warning)),
            m('.FormulaireEditFormTab', m('div', {
                key: this.tab, // Force full redraw on tab change
            }, (this as any)[this.tab + 'Tab'](form))),
            m(SaveControls, {
                dirty: this.dirty,
                loading: this.loading,
                savedAt: this.savedAt,
                inError: Object.keys(this.validationErrors).length > 0,
                history: this.history,
                onsave: () => {
                    this.loading = true;

                    form.save({
                        link_type: form.link_type(),
                        link_id: form.link_id(),
                        slug: form.slug(),
                        title: form.title(),
                        private_title: form.private_title(),
                        template: this.history.value,
                        accept_submissions: form.accept_submissions(),
                        allow_modification: form.allow_modification(),
                        max_submissions: form.max_submissions(),
                        send_confirmation_to_participants: form.send_confirmation_to_participants(),
                        notify_emails: form.notify_emails(),
                        web_confirmation_message: form.web_confirmation_message(),
                        email_confirmation_message: form.email_confirmation_message(),
                        email_notification_message: form.email_notification_message(),
                        email_confirmation_title: form.email_confirmation_title(),
                        email_notification_title: form.email_notification_title(),
                        automatic_discussion_options: form.automatic_discussion_options(),
                        permission_see_own: form.permission_see_own(),
                        permission_see_any: form.permission_see_any(),
                        permission_edit_own: form.permission_edit_own(),
                        permission_edit_any: form.permission_edit_any(),
                        show_on_creation: form.show_on_creation(),
                    }, {
                        errorHandler: this.onerror.bind(this),
                    }).then(() => {
                        this.loading = false;
                        this.dirty = false;
                        this.savedAt = new Date();
                        this.validationErrors = {};
                        // Reload template because now it will likely contain new server-side generated keys
                        this.history = new ValueHistory(form.template());
                        m.redraw();
                    }).catch(error => {
                        this.loading = false;
                        m.redraw();
                        throw error;
                    });
                },
                floating: true,
            }),
        ]);
    }

    templateTab(form: Form): Children {
        const expertMode = localStorage.getItem('formulaireExpertMode') === 'true';

        const list = new FieldList({
            configs: this.history.value,
            onchange: () => {
                this.history.save();
                this.dirty = true;
            },
            disabled: this.loading,
            validationErrors: this.validationErrors.template || {},
            expertMode,
        });

        return [
            m(FormGroup, {
                validationErrors: this.validationErrors.title,
                label: m('label', app.translator.trans(translationPrefix + 'field.title')),
            }, m('input.FormControl', {
                type: 'text',
                value: form.title(),
                oninput: withAttr('value', (value: string) => {
                    this.updateAttribute('title', value);
                }),
                disabled: this.loading,
            })),
            m('h2', [
                app.translator.trans(translationPrefix + 'section.template'),
                ' ',
                m('small', [
                    m('a', {
                        href: formulaireFormShow(form),
                        target: '_blank',
                    }, app.translator.trans(translationPrefix + (this.dirty ? 'save-for-preview' : 'preview'))),
                    ' ',
                    icon('fas fa-external-link-alt'),
                ]),
            ]),
            expertMode ? m('.Form-group', m('.Alert.Alert--warning', app.translator.trans(translationPrefix + 'expert-mode-warning'))) : null,
            list.configureView(),
        ];
    }

    placementTab(form: Form): Children {
        const linkIdOptions: any = {};

        if (form.link_type() === 'groups') {
            linkIdOptions.all = app.translator.trans(translationPrefix + 'link-options.all-groups');
            app.store.all<Group>('groups').forEach(group => {
                if (group.id() === Group.GUEST_ID || group.id() === Group.MEMBER_ID) {
                    return;
                }

                linkIdOptions[group.id()!] = group.namePlural();
            });
        } else if (form.link_type() === 'tags') {
            linkIdOptions.all = app.translator.trans(translationPrefix + 'link-options.all-tags');
            if ('flarum-tags' in flarum.extensions) {
                app.store.all<any>('tags').forEach(tag => {
                    linkIdOptions[tag.id()] = tag.name();
                });
            }
        }

        return [
            m(FormGroup, {
                validationErrors: this.validationErrors.link_type || this.validationErrors.link_id,
                label: m('label', app.translator.trans(translationPrefix + 'field.link')),
            }, [
                Select.component({
                    options: {
                        standalone: app.translator.trans(translationPrefix + 'link-options.standalone'),
                        groups: app.translator.trans(translationPrefix + 'link-options.groups'),
                        tags: app.translator.trans(translationPrefix + 'link-options.tags'),
                    },
                    value: form.link_type() || 'standalone',
                    onchange: (value: string) => {
                        this.updateAttribute('link_type', value === 'standalone' ? null : value);
                        this.updateAttribute('link_id', null);
                        this.loadTagsIfNecessary();
                    },
                    disabled: this.loading,
                }),
                ' ',
                form.link_type() !== null ? m.fragment({}, m.fragment({
                    // We need to force a refresh of the full select when the list of options changes
                    // Otherwise there is a ghost selection that sticks to the previous index instead of the selected ID
                    key: JSON.stringify(Object.keys(linkIdOptions)),
                }, Select.component({
                    options: linkIdOptions,
                    value: form.link_id() || 'all',
                    onchange: (value: string) => {
                        this.updateAttribute('link_id', value === 'all' ? null : value);
                    },
                    disabled: this.loading || this.tagsLoading,
                }))) : null,
                this.tagsLoading ? LoadingIndicator.component({
                    display: 'inline',
                }) : null,
            ]),
            form.type() !== 'standalone' ? m(FormGroup, {
                validationErrors: this.validationErrors.show_on_creation,
            }, [
                Switch.component({
                    state: form.show_on_creation(),
                    onchange: this.updateAttribute.bind(this, 'show_on_creation'),
                    disabled: this.loading,
                }, app.translator.trans(translationPrefix + 'field.show-on-creation.' + form.type())),
                (form.type() === 'discussion' && !('clarkwinkelmann-composer-page' in flarum.extensions)) ? m('.Alert', app.translator.trans(translationPrefix + 'field.show-on-creation.discussionHelp', {
                    a: composerPageExtensionLink(),
                })) : null,
            ]) : null,
            form.type() === 'discussion' ? m(FormGroup, {
                validationErrors: this.validationErrors.automatic_discussion_options,
            }, [
                Switch.component({
                    state: form.automatic_discussion_options()?.enabled,
                    onchange: (value: boolean) => {
                        const options: AutomaticDiscussionOptions = form.automatic_discussion_options() || {};
                        options.enabled = value;
                        this.updateAttribute('automatic_discussion_options', options);
                    },
                    disabled: this.loading,
                }, app.translator.trans(translationPrefix + 'field.automatic-discussion')),
                Button.component({
                    className: 'Button',
                    disabled: !form.automatic_discussion_options()?.enabled,
                    onclick: () => {
                        app.modal.show(AutomaticDiscussionOptionsModal, {
                            options: form.automatic_discussion_options() || {},
                            onchange: (options: AutomaticDiscussionOptions) => {
                                this.updateAttribute('automatic_discussion_options', options);
                                app.modal.close();
                            },
                        });
                    },
                }, app.translator.trans(translationPrefix + 'auto-discussion-options')),
            ]) : null,
        ];
    }

    accessTab(form: Form): Children {
        const showMaxSubmissions = form.type() === 'standalone' || form.isAutoLink();

        return [
            m(FormGroup, {
                validationErrors: this.validationErrors.accept_submissions,
            }, Switch.component({
                state: form.accept_submissions(),
                onchange: this.updateAttribute.bind(this, 'accept_submissions'),
                disabled: this.loading,
            }, app.translator.trans(translationPrefix + 'field.' + (form.type() === 'standalone' ? 'accept-submissions' : 'enable')))),
            form.type() === 'standalone' ? m(FormGroup, {
                validationErrors: this.validationErrors.allow_modification,
            }, Switch.component({
                state: form.allow_modification(),
                onchange: this.updateAttribute.bind(this, 'allow_modification'),
                disabled: this.loading,
            }, app.translator.trans(translationPrefix + 'field.allow-modification'))) : null,
            showMaxSubmissions ? m(FormGroup, {
                validationErrors: this.validationErrors.max_submissions,
                label: m('label', app.translator.trans(translationPrefix + 'field.max-submissions')),
            }, m('input.FormControl', {
                type: 'number',
                value: form.max_submissions(),
                oninput: withAttr('value', (value: string) => {
                    this.updateAttribute('max_submissions', value);
                }),
                disabled: this.loading,
            })) : null,
            form.type() !== 'standalone' ? [
                m('h2', app.translator.trans(translationPrefix + 'section.permissions')),
                m(FormGroup, {
                    className: 'FormulairePermissionFormGroup',
                    validationErrors: this.validationErrors.permission_see_own,
                    label: m('label', app.translator.trans(translationPrefix + 'field.permission-see-own')),
                }, PermissionDropdown.component({
                    groupIds: form.permission_see_own(),
                    onchange: this.updateAttribute.bind(this, 'permission_see_own'),
                    disabled: this.loading,
                    allowGuest: form.type() === 'user',
                    guestMeansDisabled: true,
                })),
                m(FormGroup, {
                    className: 'FormulairePermissionFormGroup',
                    validationErrors: this.validationErrors.permission_see_any,
                    label: m('label', app.translator.trans(translationPrefix + 'field.permission-see-any')),
                }, PermissionDropdown.component({
                    groupIds: form.permission_see_any(),
                    onchange: this.updateAttribute.bind(this, 'permission_see_any'),
                    disabled: this.loading,
                    allowGuest: true,
                })),
                m(FormGroup, {
                    className: 'FormulairePermissionFormGroup',
                    validationErrors: this.validationErrors.permission_edit_own,
                    label: m('label', app.translator.trans(translationPrefix + 'field.permission-edit-own')),
                }, PermissionDropdown.component({
                    groupIds: form.permission_edit_own(),
                    onchange: this.updateAttribute.bind(this, 'permission_edit_own'),
                    disabled: this.loading,
                    allowGuest: form.type() === 'user',
                    guestMeansDisabled: true,
                })),
                m(FormGroup, {
                    className: 'FormulairePermissionFormGroup',
                    validationErrors: this.validationErrors.permission_edit_any,
                    label: m('label', app.translator.trans(translationPrefix + 'field.permission-edit-any')),
                }, PermissionDropdown.component({
                    groupIds: form.permission_edit_any(),
                    onchange: this.updateAttribute.bind(this, 'permission_edit_any'),
                    disabled: this.loading,
                })),
            ] : null,
        ];
    }

    notificationsTab(form: Form): Children {
        return [
            m('.Form-group', [
                Switch.component({
                    state: form.send_confirmation_to_participants(),
                    onchange: this.updateAttribute.bind(this, 'send_confirmation_to_participants'),
                    disabled: this.loading,
                }, app.translator.trans(translationPrefix + 'field.send-confirmation-to-participants')),
                Button.component({
                    className: 'Button',
                    disabled: !form.send_confirmation_to_participants(),
                    onclick: () => {
                        app.modal.show(ConfirmationEmailOptionsModal, {
                            title: form.email_confirmation_title(),
                            message: form.email_confirmation_message(),
                            onchange: (title: string, message: string) => {
                                this.updateAttribute('email_confirmation_title', title);
                                this.updateAttribute('email_confirmation_message', message);
                                app.modal.close();
                            },
                        });
                    },
                }, app.translator.trans(translationPrefix + 'email-confirmation-options')),
            ]),
            m(FormGroup, {
                validationErrors: this.validationErrors.notify_emails,
                label: m('label', app.translator.trans(translationPrefix + 'field.notify-emails')),
            }, [
                m('input.FormControl', {
                    type: 'text',
                    value: form.notify_emails(),
                    oninput: withAttr('value', (value: string) => {
                        this.updateAttribute('notify_emails', value);
                    }),
                    disabled: this.loading,
                }),
                Button.component({
                    className: 'Button',
                    disabled: !form.notify_emails(),
                    onclick: () => {
                        app.modal.show(ConfirmationEmailOptionsModal, {
                            title: form.email_notification_title(),
                            message: form.email_notification_message(),
                            onchange: (title: string, message: string) => {
                                this.updateAttribute('email_notification_title', title);
                                this.updateAttribute('email_notification_message', message);
                                app.modal.close();
                            },
                        });
                    },
                }, app.translator.trans(translationPrefix + 'email-notification-options'))
            ]),
            m(FormGroup, {
                validationErrors: this.validationErrors.web_confirmation_message,
                label: m('label', app.translator.trans(translationPrefix + 'field.web-confirmation-message')),
            }, m(RichTextField, {
                value: form.web_confirmation_message(),
                onchange: withAttr('value', (value: string) => {
                    this.updateAttribute('web_confirmation_message', value);
                }),
                disabled: this.loading,
            })),
        ];
    }

    exportTab(form: Form): Children {
        return [
            // TODO: move import/export to popup and translate
            m(FormGroup, {
                label: m('label', 'Import/Export (you must refresh the page before copying for the latest version)'),
            }, m('textarea.FormControl', {
                value: JSON.stringify(form.template(), null, 4),
                oninput: withAttr('value', (value: string) => {
                    try {
                        this.history.value = JSON.parse(value);
                        this.history.save();
                        this.dirty = true;
                    } catch (e) {
                        alert('Invalid JSON');
                    }
                }),
                disabled: this.loading,
            })),
        ];
    }

    updateAttribute(attribute: string, value: any) {
        // This won't happen in practice but it pleases typescript
        if (!this.form) {
            return;
        }

        // If the title is being edited, and both title and private title were identical, edit both together
        if (attribute === 'title' && this.form.title() === this.form.private_title()) {
            this.form.pushAttributes({
                private_title: value,
            });
        }

        this.form.pushAttributes({
            [attribute]: value,
        });

        this.dirty = true;
        delete this.validationErrors[attribute];
    }

    warnings(form: Form): Children[] {
        const warnings: Children[] = [];

        const finalPermission = (localAbility: string, globalAbility: string) => {
            const local = (form as any)['permission_' + localAbility]();

            if (local !== null) {
                return local;
            }

            return app.forum.attribute<any>('formulaireGlobalPermissions')[globalAbility];
        };

        function doesntAllowOnly(permissionValue: string[], groupId: string) {
            return Array.isArray(permissionValue) && permissionValue.indexOf(groupId) === -1;
        }

        if (form.type() !== 'standalone' && form.show_on_creation()) {
            if (form.type() === 'user') {
                const seeOwnPermission = finalPermission('see_own', 'formulaire.seeOwnUser');
                const editOwnPermission = finalPermission('edit_own', 'formulaire.editOwnUser');

                if (doesntAllowOnly(seeOwnPermission, Group.GUEST_ID) || doesntAllowOnly(editOwnPermission, Group.GUEST_ID)) {
                    warnings.push(app.translator.trans(translationPrefix + 'warnings.show-on-signup-invalid-permissions'));
                }
            } else {
                const seeOwnPermission = finalPermission('see_own', 'discussion.seeOwnFormulaire');
                const editOwnPermission = finalPermission('edit_own', 'discussion.editOwnFormulaire');

                if (doesntAllowOnly(seeOwnPermission, Group.MEMBER_ID) || doesntAllowOnly(editOwnPermission, Group.MEMBER_ID)) {
                    warnings.push(app.translator.trans(translationPrefix + 'warnings.show-on-composer-invalid-permissions'));
                }
            }
        }

        if (form.type() !== 'standalone' && !form.accept_submissions()) {
            warnings.push(app.translator.trans(translationPrefix + 'warnings.linked-form-is-draft'));
        }

        if (form.isAutoLink()) {
            warnings.push(app.translator.trans(translationPrefix + 'warnings.auto-link-permission-warning'));
        }

        return warnings;
    }

    loadTagsIfNecessary() {
        if (this.tab !== 'placement' || !this.form || this.form.link_type() !== 'tags' || this.tagsLoaded || !('flarum-tags' in flarum.extensions)) {
            return;
        }

        // tagList should be available since Flarum 1.0
        // @ts-ignore
        if (!app.tagList) {
            console.warn('[formulaire] Cannot load tag list');

            return;
        }

        this.tagsLoading = true;

        // We don't need the parent relationship for now, but if we don't include any TagListState won't load anything
        // @ts-ignore
        app.tagList.load(['parent']).then(() => {
            this.tagsLoading = false;
            this.tagsLoaded = true;

            m.redraw();
        });
    }
}
